import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, of, switchMap, withLatestFrom } from 'rxjs';
import * as ClientActions from './client.actions';
import { selectClients, selectClientsQuery } from './client.selectors';
import { ClientService } from '../services/client.service';
import { ClientDto } from '@shared/dto';
import { listResultActions$ } from '@shared/utils/pagination.utils';

@Injectable()
export class ClientEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly clientService = inject(ClientService);

  readClients$ = createEffect(() => this.actions$.pipe(
    ofType(ClientActions.readClients),
    exhaustMap(({ params, query }) =>
      listResultActions$(
        query ? this.clientService.read(params, { pagination: query }) : this.clientService.read(params),
        query,
        ({ data, total }) => ClientActions.readClientsSuccess({ data, total }),
        (error) => ClientActions.readClientsFailure({ error }),
      )),
  ));

  // Detail: resolve from the already-loaded list first (no HTTP), otherwise fetch
  // the single entity. The store is the source of truth that both the detail page
  // and the breadcrumb read from.
  readClient$ = createEffect(() => this.actions$.pipe(
    ofType(ClientActions.readClient),
    withLatestFrom(this.store.select(selectClients)),
    switchMap(([{ clientId }, list]) => {
      const cached = list?.find((client) => client.id === clientId);
      if (cached) {
        return of(ClientActions.readClientSuccess({ client: cached }));
      }

      return this.clientService.readOne({ clientId }).pipe(
        map((client) => ClientActions.readClientSuccess({ client })),
        catchError((error) => of(ClientActions.readClientFailure({ error }))),
      );
    }),
  ));

  createClient$ = createEffect(() => this.actions$.pipe(
    ofType(ClientActions.createClient),
    exhaustMap(({ dto }) => this.clientService.create(dto).pipe(
      map((client: ClientDto) => ClientActions.createClientSuccess({ client })),
      catchError((error) => of(ClientActions.createClientFailure({ error })))
    ))
  ));

  updateClient$ = createEffect(() => this.actions$.pipe(
    ofType(ClientActions.updateClient),
    exhaustMap(({ clientId, dto }) => this.clientService.update({ clientId }, dto).pipe(
      map((client: Partial<ClientDto>) => ClientActions.updateClientSuccess({ client })),
      catchError((error) => of(ClientActions.updateClientFailure({ error })))
    ))
  ));

  deleteClient$ = createEffect(() => this.actions$.pipe(
    ofType(ClientActions.deleteClient),
    exhaustMap(({ clientId }) => this.clientService.delete({ clientId }).pipe(
      map((client: ClientDto) => ClientActions.deleteClientSuccess({ client })),
      catchError((error) => of(ClientActions.deleteClientFailure({ error })))
    ))
  ));

  restoreClient$ = createEffect(() => this.actions$.pipe(
    ofType(ClientActions.restoreClient),
    exhaustMap(({ clientId }) => this.clientService.restore({ clientId }).pipe(
      map((client: ClientDto) => ClientActions.restoreClientSuccess({ client })),
      catchError((error) => of(ClientActions.restoreClientFailure({ error })))
    ))
  ));

  // After a mutation, refetch the current page so counts/pagination stay correct.
  reloadAfterMutation$ = createEffect(() => this.actions$.pipe(
    ofType(
      ClientActions.createClientSuccess,
      ClientActions.updateClientSuccess,
      ClientActions.deleteClientSuccess,
      ClientActions.restoreClientSuccess,
    ),
    withLatestFrom(this.store.select(selectClientsQuery)),
    map(([, query]) => ClientActions.readClients(undefined, query ?? undefined)),
  ));
}
